/**
 * ─── [P7B §5] WHAT AN ARRANGEMENT IS FOR, BESIDE WHAT KIND IT IS ─────────────────────────────
 *
 * An arrangement may carry a `type`: data saying which recurring event raised it. A standard order
 * carries NONE — the key is ABSENT from the stored document and from every DTO, never `null` — and
 * an order created from the Durga Puja template set carries `durga-puja-2026`.
 *
 * ⚠️ IT IS NOT A THIRD `kind`. `kind` (`STANDARD` · `ROUND_THE_CLOCK`) says how an order came to
 * exist and how it behaves; `type` says nothing about behaviour at all. A typed order is an ordinary
 * `STANDARD` order in every respect — publish, fill, archive, attendance — and the type only decides
 * which lists show it. ⚠️ [amended TYPE-FILTER] The list query is still untyped by default
 * ([P7B §5b]), but Manage Arrangements now ASKS for `any` and lets the operator narrow it ("Order
 * type"), so the user's request reverses "182 Puja orders cannot flood it" for that page alone.
 *
 * ⚠️ A CLOSED REGISTRY, NEVER A FREE STRING. A typo cannot mint a tab, a filter value or a stored
 * value: the model's `enum`, the list query's `z.enum` and the web's tab set are all built from these
 * keys. Next year's set is one line here, plus its template set imported.
 *
 * ⚠️ THE KEY IS `<event>-<year>`, AND A TEMPLATE RUN DERIVES IT (`arrangementTypeFor`) — a run whose
 * derived key is not registered is refused, so a run cannot create orders of an unregistered type.
 *
 * Runtime values only (no zod): `pwa` and `client-core` may import this barrel.
 */
export const ARRANGEMENT_TYPE_KEYS = ['durga-puja-2026'] as const;
export type ArrangementType = (typeof ARRANGEMENT_TYPE_KEYS)[number];

export type ArrangementTypeInfo = {
  /** What a person reads on the tab and the record. */
  readonly label: string;
  /** The template set's `event` this type is created from. */
  readonly event: string;
  /** The year the orders are for — never the template set's source year. */
  readonly year: number;
};

export const ARRANGEMENT_TYPES = {
  'durga-puja-2026': { label: 'Durga Puja PA', event: 'durga-puja', year: 2026 },
} as const satisfies Record<ArrangementType, ArrangementTypeInfo>;

/**
 * The list query's value for "untyped work only" — the default, so Manage Arrangements shows what it
 * always showed without the operator asking ([P7B §5b]). A word, never `null`, because a query string
 * cannot carry `null`.
 */
export const ARRANGEMENT_TYPE_NONE = 'none' as const;

/**
 * [TYPE-FILTER] The list query's value for "every type" — no type term at all. Manage Arrangements sends
 * it by default; the server's default stays `none`, so every other caller reads what it always read.
 */
export const ARRANGEMENT_TYPE_ANY = 'any' as const;

/** What the list query's `type` may be: untyped only, every type, or one registered type. */
export type ArrangementTypeFilter = typeof ARRANGEMENT_TYPE_NONE | typeof ARRANGEMENT_TYPE_ANY | ArrangementType;

/** A `type` that narrows the list — untyped only, or one registered type; never `any`. */
export type ArrangementTypeNarrowing = Exclude<ArrangementTypeFilter, typeof ARRANGEMENT_TYPE_ANY>;

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [DP2026] WHICH BOOKLET MADE A TYPED ORDER. Two template sets make orders of ONE type: the 2025 booklet
 *  made `durga-puja-2026` drafts before the 2026 booklet existed, and the 2026 booklet makes them now.
 *
 *  ⚠️ THE "CURRENT" BOOKLET OF A TYPE IS THE SET WHOSE KEY IS THE TYPE'S OWN KEY. A set's key is
 *  `<event>-<source year>` and a type's is `<event>-<year>` (`arrangementTypeFor`), so the 2026 booklet's
 *  key IS the type `durga-puja-2026`. An order's `template_source.set_key` equal to its `type` was made by
 *  its own year's booklet; any other set key is an EARLIER booklet's. A typed order made from no booklet (a
 *  corrigendum or a copy) carries no set key and belongs to neither.
 *
 *  `booklet` narrows the Durga Puja PA list (`all` — the default — `current` or `earlier`).
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const ARRANGEMENT_BOOKLETS = ['all', 'current', 'earlier'] as const;
export type ArrangementBooklet = (typeof ARRANGEMENT_BOOKLETS)[number];

export const isArrangementBooklet = (value: unknown): value is ArrangementBooklet =>
  (ARRANGEMENT_BOOKLETS as readonly unknown[]).includes(value);

export const isArrangementType = (value: unknown): value is ArrangementType =>
  (ARRANGEMENT_TYPE_KEYS as readonly unknown[]).includes(value);

export const isArrangementTypeNarrowing = (value: unknown): value is ArrangementTypeNarrowing =>
  value === ARRANGEMENT_TYPE_NONE || isArrangementType(value);

/**
 * [DP2026] A DRAFT MADE FROM ITS OWN TYPE'S BOOKLET — the set key equal to the type key, `durga-puja-2026` made
 * from `durga-puja-2026`. Such a draft is listed on its type's page ALONE until it is published; Manage
 * Arrangements sends `currentBookletDrafts=exclude`. This is the TypeScript twin of that MQL in
 * `arrangementListFilter`, for the screens that must know where such a draft is listed (its back link).
 */
export const isCurrentBookletDraft = (order: {
  readonly type?: string | null | undefined;
  readonly status: string;
  readonly templateSource?: { readonly setKey: string } | null | undefined;
}): boolean => order.status === 'DRAFT' && isArrangementType(order.type) && order.templateSource?.setKey === order.type;

/** The registered type for an event and a year, or `null` when none is registered. */
export const arrangementTypeFor = (event: string, year: number): ArrangementType | null =>
  ARRANGEMENT_TYPE_KEYS.find((key) => ARRANGEMENT_TYPES[key].event === event && ARRANGEMENT_TYPES[key].year === year) ??
  null;

/**
 * [P7B §4] Who may work with templates: `arrangement.write` and a commissionerate seat — a super admin,
 * or a nodal admin seated at the root (by POSITION, `isSeatedAtCommissionerate`). The server's 403 and
 * the web's tooltip read this one sentence, so they cannot drift.
 */
export const TEMPLATE_AUTHOR_REFUSAL =
  'Templates are used from the commissionerate: a super admin, or a nodal admin seated at the CP office.';
